/**
 * PDF Generator for Leadsheets
 * Generates PDF with precise chord positioning using jsPDF
 */

import jsPDF from 'jspdf';
import { ParsedLine } from './leadsheetParser';
import { analyzeSectionsAndFontSize, breakLineIfNeeded } from './pdf/pdfLayout';
import { renderSectionHeader, renderTextLine, renderLineWithChords, calculateSectionSpacing } from './pdf/pdfRenderer';
import { renderPDFHeader } from './pdf/pdfHeader';

interface PDFOptions {
  title: string;
  artist: string;
  fontSize: number;
  chordFontSize: number;
  chordColor: string;
  columns: number;
  transposedKey?: string;
  textOnly?: boolean;
}

/**
 * Generate PDF from parsed lyrics with improved 2-column layout
 */
export function generateLeadsheetPDF(
  parsedLines: ParsedLine[],
  options: PDFOptions
): void {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginLeft = 15;
  const marginRight = 15;
  const marginTop = 20;
  const marginBottom = 20;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const columnGap = 10; // Gap between columns

  // Render header
  renderPDFHeader(doc, pageWidth, {
    title: options.title,
    artist: options.artist,
    transposedKey: options.transposedKey,
  });

  let yPosition = 45;

  // Set monospace font for lyrics
  doc.setFont('courier', 'normal');
  
  // Calculate column widths
  const columnWidth = options.columns === 2 ? (contentWidth - columnGap) / 2 : contentWidth;

  // Analyze sections and adjust font size if needed for 2-column layout
  const { adjustedFontSize, adjustedChordFontSize } = analyzeSectionsAndFontSize(
    parsedLines,
    {
      fontSize: options.fontSize,
      chordFontSize: options.chordFontSize,
      columns: options.columns,
    },
    doc,
    columnWidth
  );

  // Pre-process: Break long lines if needed
  const processedLines: ParsedLine[] = [];
  for (const line of parsedLines) {
    const brokenLines = breakLineIfNeeded(line, columnWidth, adjustedFontSize, doc);
    processedLines.push(...brokenLines);
  }

  // Re-calculate section indices after line breaking
  const finalSectionIndices: number[] = [];
  for (let i = 0; i < processedLines.length; i++) {
    if (processedLines[i].isSection) {
      finalSectionIndices.push(i);
    }
  }

  // Determine column switch point for 2-column layout
  let columnSwitchIndex = processedLines.length; // Default: no switch (all in left column)
  if (options.columns === 2 && finalSectionIndices.length > 0) {
    // Split sections equally: left column gets ceiling(sections / 2)
    const sectionsPerColumn = Math.ceil(finalSectionIndices.length / 2);
    
    if (sectionsPerColumn < finalSectionIndices.length) {
      // Switch to right column at the Nth section
      columnSwitchIndex = finalSectionIndices[sectionsPerColumn];
    }
  }

  let currentColumn = 0;
  let columnXPosition = marginLeft;

  // Render each line
  for (let i = 0; i < processedLines.length; i++) {
    const line = processedLines[i];

    // Check for column switch (only for 2-column layout)
    if (options.columns === 2 && i === columnSwitchIndex && currentColumn === 0) {
      currentColumn = 1;
      columnXPosition = marginLeft + columnWidth + columnGap;
      yPosition = 45; // Reset to top for second column
    }

    // Check for page break
    if (yPosition > pageHeight - marginBottom - 20) {
      doc.addPage();
      yPosition = marginTop;
      currentColumn = 0;
      columnXPosition = marginLeft;
    }

    // Section headers
    if (line.isSection) {
      // Smart spacing algorithm
      // Space before section (except first one in each column): 1.5 line breaks
      if (i > 0 && !(i === columnSwitchIndex && options.columns === 2)) {
        yPosition += (adjustedFontSize * 0.65);
      }
      
      yPosition = renderSectionHeader(doc, line, columnXPosition, yPosition, adjustedFontSize);
      
      // Space after section header: Check if next line has chords (kun hvis ikke textOnly)
      const nextLine = i + 1 < processedLines.length ? processedLines[i + 1] : null;
      yPosition += calculateSectionSpacing(nextLine, adjustedFontSize, adjustedChordFontSize, options.textOnly || false);
      continue;
    }

    // Lines without chords
    if (line.chords.length === 0) {
      yPosition = renderTextLine(doc, line, columnXPosition, yPosition, adjustedFontSize);
      continue;
    }

    // Lines with chords - render chords above text (kun hvis ikke textOnly)
    yPosition = renderLineWithChords(doc, line, columnXPosition, yPosition, {
      fontSize: adjustedFontSize,
      chordFontSize: adjustedChordFontSize,
      chordColor: options.chordColor,
      textOnly: options.textOnly || false,
    });
  }

  // Open in new tab
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
}

